<script setup lang="ts">
import { joinURL, withQuery } from 'ufo'

const props = withDefaults(
  defineProps<{
    post: PostItem;
  }>(),
  {}
);

const project = computed(() => props.post.type === PostType.Project ? props.post : props.post.related_project);

const enabled = computed(
  () =>
    !!project.value?.project_details_enabled
);

const normal = computed(() => project.value?.project_details_title_normal);
const decorative = computed(() => project.value?.project_details_title_decorative);

const showNormal = computed(() => !!normal.value);
const showDecorative = computed(() => !!decorative.value);

const { n } = useI18n();

const areaDataIcon = useMemoize(
  (type: "building_area" | "usable_area" | "pitch_angle") => {
    return {
      building_area: "ns:home",
      usable_area: "ns:move-3d",
      pitch_angle: "ns:move-down-right",
    }[type];
  }
);

const areaDataLabel = useMemoize(
  (type: "building_area" | "usable_area" | "pitch_angle") => {
    return {
      building_area: "Powierzchnia\nzabudowy",
      usable_area: "Powierzchnia\nużytkowa",
      pitch_angle: "Kąt\nnachylenia",
    }[type];
  }
);

const areaDataValue = useMemoize(
  (value: number, type: "building_area" | "usable_area" | "pitch_angle") => {
    return {
      building_area: `${n(value)} m²`,
      usable_area: `${n(value)} m²`,
      pitch_angle: `${n(value)}°`,
    }[type];
  }
);

const areaData = computed(() =>
  project.value?.project_details_area_data?.map((item) => ({
    icon: areaDataIcon(item.type),
    label: areaDataLabel(item.type),
    value: areaDataValue(item.value, item.type),
  }))
);

const specs = computed(() => project.value?.project_details_specification_value);

const { sanitizeHtml } = useSanitize();

const project_file = computed(() => project.value?.project_details_file_value);

const { url } = useDirectus();
const { isLoggedIn, user, openAuthDialog } = useAuth()

const downloadUri = (uri: string, name: string): void => {
  const link = document.createElement('a')
  link.href = uri
  link.download = name
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

const downloadFile = async (): Promise<void> => {
  const fileId = project_file.value?.id || (project_file.value as any as string) || '';
  const fileName = project_file.value?.filename_download || 'wycena.pdf';

  downloadUri(
    withQuery(
      joinURL(url.href, 'assets', fileId),
      { download: true }
    ),
    fileName
  )
}

const tryDownloadFile = async (): Promise<boolean> => {
  if (isLoggedIn.value) {
    await downloadFile()
    return true;
  } else {
    openAuthDialog(AuthView.Login)
    return false;
  }
}

const model = reactive({
  phone: "",
});

const errors = reactive({
  phone: undefined as string | undefined,
});

const { createItem } = useDirectusItems();


const saveDownloadInfo = async (): Promise<boolean> => {
  const data = {
    post: project.value?.id,
    phone: model.phone,
    email: user.value?.email,
    name: [user.value?.first_name, user.value?.last_name].filter(Boolean).join(' '),
    date: new Date().toISOString(),
  }

  return await createItem("project_download_log", data)
    .then(() => true)
    .catch(() => false)
}

const isLoading = ref(false);

const handleSubmit = async (): Promise<void> => {
  errors.phone = undefined;

  if (!model.phone) {
    errors.phone = "Numer telefonu jest wymagany";
    return;
  }

  isLoading.value = true;

  const donwloaded = await tryDownloadFile();

  if (donwloaded) {
    await saveDownloadInfo().finally(() => {
      isLoading.value = false;
    });
  } else {
    isLoading.value = false;
  }
}
</script>

<template>
  <section v-if="enabled" class="post-content-project p-4 rounded-3xl bg-[#F8F8F8]">
    <div class="post-content-project__wrapper flex flex-col items-stretch gap-4">
      <div class="post-content-project__title py-1">
        <h2 :class="['text-[1.75rem] lg:text-[2.5rem] leading-[2.5rem]']">
          <span v-if="showNormal" class="inline">
            <slot name="normal" v-bind="{ props }">
              {{ normal }}
            </slot>
          </span>
          <span v-if="showNormal && showDecorative" class="tracking-[0.25rem]">&emsp14;</span>
          <span v-if="showDecorative" class="inline font-display tracking-[0.3125rem]">
            <slot name="decorative" v-bind="{ props }">
              {{ decorative }}
            </slot>
          </span>
        </h2>
      </div>

      <div class="post-content-project__details">
        <div
          class="post-content-project__details-wrapper flex flex-col lg:flex-row gap-4 lg:gap-16 justify-between items-stretch">
          <div v-if="props.post.project_details_price_value"
            class="post-content-project__price flex flex-col items-stretch gap-2">
            <div class="post-content-project__price-title">
              <h3 class="font-semibold text-lg leading-8">Cena zakupu</h3>
            </div>

            <div class="post-content-project__price-content grow flex justify-stretch flex-wrap">
              <PostContentProjectData :icon="`ns:banknote`" :label="`Cena\nprojektu`" :value="[
                n(parseFloat(props.post.project_details_price_value)),
                props.post.project_details_price_currency,
              ]
                .filter(Boolean)
                .join(' ')
                " class="grow basis-24 lg:basis-36" tonal />
            </div>
          </div>

          <div v-if="areaData && areaData.length > 0"
            class="post-content-project__data grow flex flex-col items-stretch gap-2">
            <div class="post-content-project__data-title">
              <h3 class="font-semibold text-lg leading-8">
                Dane powierzchniowe
              </h3>
            </div>

            <div class="post-content-project__data-content grow flex justify-stretch flex-wrap">
              <PostContentProjectData v-for="item in areaData" :icon="item.icon" :label="item.label" :value="item.value"
                class="grow basis-24 lg:basis-32" />
            </div>
          </div>
        </div>
      </div>

      <div v-if="specs" class="post-content-project__specs">
        <div class="post-content-project__specs-wrapper flex flex-col items-stretch gap-2">
          <div class="post-content-project__specs-title">
            <h3 class="font-semibold text-lg leading-8">
              Specyfikacja materiałowa
            </h3>
          </div>

          <div :class="[
            'post-content-project__specs-content',
            'text-base leading-6',
            'text-black prose prose-ul:text-black prose-ol:text-black prose-li:m-0 prose-li:marker:text-black',
          ]" v-html="sanitizeHtml(specs)"></div>
        </div>
      </div>

      <form v-if="!!project_file" class="post-content-project__quote" @submit.prevent="handleSubmit">
        <div class="post-content-project__quote-wrapper flex flex-col items-stretch gap-4">
          <div v-if="isLoggedIn" class="post-content-project__quote-phone grid md:grid-cols-2 gap-4">
            <AppInput label="Numer telefonu" v-model="model.phone" type="tel" name="phone" autocomplete="tel"
              :disabled="isLoading" :error="errors.phone !== undefined" :hint="errors.phone" required />
          </div>

          <div class="post-content-project__quote-download">
            <AppBtn type="submit" :disabled="isLoading">Pobierz wycenę</AppBtn>
          </div>
        </div>
      </form>

      <!-- <div class="post-content-project__actions">
        <AppBtn
          variant="outline"
          size="sm"
          type="nuxt-link"
          :to="localePath({ name: 'contact' })"
        >
          <span>Skontaktuj się</span>
        </AppBtn>
      </div> -->
    </div>
  </section>
</template>
